import React, { ChangeEvent, FC, FormEvent, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import get from "lodash/get";

import { corporateBuyPlans } from "../../../../config/router/routes";
import { useRecurlyToken } from "../../../../utils/recurly/useRecurlyToken";
import Alert from "../../../../components/Alert";
import ButtonPrimary from "../../../../components/ButtonPrimary";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import Input from "../../../../components/Input";
import PasswordInput from "../../../../components/PasswordInput";
import PaymentFormContent from "../../../../components/PaymentFormContent";
import SelectedPlan from "../../components/SelectedPlan";
import { PLANS_FEATURES } from "../../../../constants/common";
import useCorporateBuyFormState from "../../hooks/use-corporate-buy-form-state";
import { ICorporateBuyFormStateType } from "../../../../utils/buy/types";
import userService from "../../../../services/api/user";
import Toaster from "../../../../services/toaster";
import { BillingToken } from "../../../../services/api/user/types";
import { getUsername } from "../../../../utils/common";

import "./styles.scss";

const initialBuyFormState: ICorporateBuyFormStateType = {
  name: "",
  businessName: "",
  username: "",
  businessEmail: "",
  password: "",
};

export const CorporateBuy: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const getRecurlyToken = useRecurlyToken();
  const [isPaymentDataValid, setIsPaymentDataValid] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string>("");
  const [buyFromState, setBuyFormState] = useCorporateBuyFormState(
    initialBuyFormState,
  );
  const selectedPlan = get(location, ["state", "plan"]);
  const planIndex = get(location, ["state", "index"]);
  const offeringCode = get(location, ["state", "offeringCode"]);
  const tenantCode = get(location, ["state", "tenantCode"]);
  const isFormValid =
    isPaymentDataValid &&
    Boolean(buyFromState.name) &&
    Boolean(buyFromState.businessName) &&
    Boolean(buyFromState.businessEmail) &&
    Boolean(buyFromState.password);

  if (!selectedPlan) {
    history.push(corporateBuyPlans.path);
    return null;
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBuyFormState({ name: event.target.value });
  };

  const handleBusinessNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBuyFormState({ businessName: event.target.value });
  };

  const handleBusinessEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBuyFormState({ businessEmail: event.target.value });
  };

  const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBuyFormState({ username: event.target.value });
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBuyFormState({ password: event.target.value });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordErrors("");
    try {
      const { id } = await getRecurlyToken(event.target as HTMLFormElement);
      const {
        name,
        businessEmail,
        businessName,
        password,
        username,
      } = buyFromState;
      const billingToken: BillingToken = {
        kind: "RECURLY_TOKEN",
        // eslint-disable-next-line @typescript-eslint/camelcase
        recurly_token: id,
      };
      const user = {
        name,
        password,
        email: businessEmail,
        username: getUsername(username, tenantCode),
        // eslint-disable-next-line @typescript-eslint/camelcase
        customer_name: businessName,
        currency: (selectedPlan?.geoCost?.currency ||
          selectedPlan?.currency) as string,
        // eslint-disable-next-line @typescript-eslint/camelcase
        sku_id: selectedPlan?.sku_id as string,
        // eslint-disable-next-line @typescript-eslint/camelcase
        offering_code: offeringCode as string,
        // eslint-disable-next-line @typescript-eslint/camelcase
        billing_token: billingToken,
        // eslint-disable-next-line @typescript-eslint/camelcase
        communication_locale: navigator.language,
      };

      await userService.create(user);
      Toaster.success({
        content: t("accountCreated"),
      });
    } catch (err) {
      const parsePasswordErrors = err.response?.data?.details?.some(
        (item: any) => {
          return item.path === "password";
        },
      );

      if (parsePasswordErrors) {
        setPasswordErrors("validationFailed");
      }

      Toaster.error({
        content: t("somethingWentWrong"),
      });
    }
  };

  return (
    <div className="ams-corporate-buy-page">
      <h1 className="ams-corporate-buy-page__header">
        {t("signUpForBusiness")}
      </h1>
      <form className="ams-corporate-buy-page__content" onSubmit={handleSubmit}>
        <div className="ams-corporate-buy-page__column">
          <section className="ams-corporate-buy-page__form">
            <h2 className="ams-corporate-buy-page__form-label">
              {t("yourInformation")}
            </h2>
            <FormGroup>
              <Label>{t("yourName")}</Label>
              <Input
                value={buyFromState.name}
                required
                onChange={handleNameChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("businessName")}</Label>
              <Input
                value={buyFromState.businessName}
                required
                onChange={handleBusinessNameChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("username")}</Label>
              <Input
                type="text"
                value={buyFromState.username}
                required
                onChange={handleUsernameChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("businessEmail")}</Label>
              <Input
                type="email"
                value={buyFromState.businessEmail}
                required
                onChange={handleBusinessEmailChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("password")}</Label>
              <PasswordInput
                required
                value={buyFromState.password}
                onChange={handlePasswordChange}
                error={!!passwordErrors}
              />
              {!!passwordErrors && (
                <div className="ams-password-error__password-alert">
                  <Alert
                    className="ams-password-error__error-alert"
                    variant="error"
                    content={
                      <ul className="ams-password-error__error-list">
                        <li>{t(`error.${passwordErrors}`)}</li>
                      </ul>
                    }
                  />
                </div>
              )}
            </FormGroup>
          </section>
          <section className="ams-corporate-buy-page__form mt-3xl">
            <h2 className="ams-corporate-buy-page__form-label">
              {t("billingInformation")}
            </h2>
            <PaymentFormContent
              showLocation
              onValidationChange={setIsPaymentDataValid}
            />
          </section>
        </div>
        <div className="ams-corporate-buy-page__column">
          <h2 className="ams-corporate-buy-page__form-label">
            {t("yourPlan")}
          </h2>
          <SelectedPlan
            plan={selectedPlan}
            features={PLANS_FEATURES[planIndex]}
          />
          <ButtonPrimary
            type="submit"
            className="ams-button--large ams-button--full-width"
            disabled={!isFormValid}
          >
            {t("purchasePlan")}
          </ButtonPrimary>
          <div className="ams-corporate-buy-page__disclaimer">
            <Trans i18nKey="purchaseSubscriptionDislaimer">
              {
                "By continuing with your purchase, you agree to the Golden Frog "
              }
              <a href="/" rel="noopener noreferrer">
                Terms of Service
              </a>
              .
            </Trans>
          </div>
        </div>
      </form>
    </div>
  );
};
